// src/pages/Dashboard.js
//
// Dashboard operativo conectado a datos reales.
//
// Consume el ReportesController heredado:
//   GET /api/pos/reportes/dashboard/{empresaId}
//   GET /api/pos/reportes/serie-diaria
//   GET /api/pos/reportes/ventas-por-categoria
//   GET /api/pos/reportes/productos-mas-vendidos
//   GET /api/pos/reportes/stock-critico/{empresaId}
//   GET /api/pos/reportes/ventas-por-metodo-pago
//
// KPIs mostrados:
//   - Ventas del período (con delta % vs período anterior)
//   - Utilidad bruta (con delta)
//   - Transacciones (con delta)
//   - Ticket promedio (con delta)
//   - Snapshot del día: ventas hoy, clientes nuevos hoy, cajas abiertas,
//     stock bajo, facturas pendientes
//
// Charts:
//   - Serie diaria Ventas vs Utilidad (línea)
//   - Ventas por categoría (pie)
//   - Top productos vendidos (bar horizontal)
//   - Stock crítico (tabla)

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, ToggleButton, ToggleButtonGroup, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, LinearProgress, Stack, Paper,
  CircularProgress, Alert, IconButton, Divider,
} from '@mui/material';
import TrendingUpIcon    from '@mui/icons-material/TrendingUp';
import TrendingDownIcon  from '@mui/icons-material/TrendingDown';
import PaidIcon          from '@mui/icons-material/Paid';
import ReceiptIcon       from '@mui/icons-material/Receipt';
import ShoppingCartIcon  from '@mui/icons-material/ShoppingCart';
import WarningAmberIcon  from '@mui/icons-material/WarningAmber';
import PeopleIcon        from '@mui/icons-material/People';
import PointOfSaleIcon   from '@mui/icons-material/PointOfSale';
import ReceiptLongIcon   from '@mui/icons-material/ReceiptLong';
import RefreshIcon       from '@mui/icons-material/Refresh';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { useApp } from '../context/AppContext';
import { reportes as apiReportes } from '../api/posApi';

const fmtCOP = (n) => (Number(n) || 0).toLocaleString('es-CO',
  { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });

const KpiCard = ({ icon, label, value, delta, color = 'primary.main' }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
          <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>{value}</Typography>
          {delta !== null && delta !== undefined && Number.isFinite(delta) && (
            <Chip
              size="small"
              icon={delta >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
              label={`${delta >= 0 ? '+' : ''}${delta.toFixed(1)}% vs período anterior`}
              color={delta >= 0 ? 'success' : 'error'}
              variant="outlined"
              sx={{ mt: 1, height: 22, fontSize: 11 }}
            />
          )}
        </Box>
        <Box sx={{
          width: 44, height: 44, borderRadius: 2, bgcolor: color, opacity: 0.15,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {React.cloneElement(icon, { sx: { color } })}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const MiniCard = ({ icon, label, value, color = 'text.primary' }) => (
  <Paper variant="outlined" sx={{ p: 1.5 }}>
    <Stack direction="row" alignItems="center" spacing={1.2}>
      <Box sx={{ color }}>{icon}</Box>
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body1" fontWeight={700} sx={{ color }}>{value}</Typography>
      </Box>
    </Stack>
  </Paper>
);

const Dashboard = () => {
  const { empresa, sucursalActivaId, notify } = useApp();
  const empresaId = empresa?.empresaId || empresa?.EmpresaId;

  const [periodo, setPeriodo] = useState('30');    // días
  const [snapshot, setSnapshot] = useState(null);
  const [serie, setSerie] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [topProductos, setTopProductos] = useState([]);
  const [stockCritico, setStockCritico] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [tick, setTick] = useState(0);

  const cargar = useCallback(async () => {
    if (!empresaId) return;
    setCargando(true);
    const dias = parseInt(periodo, 10);
    const desdePer = new Date(Date.now() - (dias - 1) * 86400000).toISOString();
    const hastaPer = new Date().toISOString();

    try {
      const [snap, ser, cats, top, sc] = await Promise.all([
        apiReportes.dashboard(empresaId, sucursalActivaId),
        apiReportes.serieDiaria(empresaId, sucursalActivaId, dias),
        apiReportes.ventasPorCategoria(empresaId, sucursalActivaId, desdePer, hastaPer),
        apiReportes.productosMasVendidos(empresaId, sucursalActivaId, desdePer, hastaPer, 10),
        apiReportes.stockCritico(empresaId, sucursalActivaId),
      ]);
      setSnapshot(snap);
      setSerie(Array.isArray(ser) ? ser : []);
      setCategorias(Array.isArray(cats) ? cats : []);
      setTopProductos(Array.isArray(top) ? top : []);
      setStockCritico(Array.isArray(sc) ? sc : []);
    } catch (e) {
      notify(e.mensajeUsuario || 'No se pudo cargar el dashboard.', 'error');
    } finally { setCargando(false); }
  }, [empresaId, sucursalActivaId, periodo, notify]);

  useEffect(() => { cargar(); }, [cargar, tick]);

  // Totales del período + deltas comparando primera vs segunda mitad
  // (aproximación cuando no hay endpoint dedicado a período anterior).
  const totales = useMemo(() => {
    const ventas = serie.reduce((s, d) => s + Number(d.ventas || 0), 0);
    const utilidad = serie.reduce((s, d) => s + Number(d.utilidad || 0), 0);
    const trans = serie.reduce((s, d) => s + Number(d.transacciones || 0), 0);
    const ticketProm = trans > 0 ? ventas / trans : 0;
    const margen = ventas > 0 ? (utilidad / ventas) * 100 : 0;

    // Delta: compara última mitad del período vs primera mitad
    const half = Math.floor(serie.length / 2);
    if (half < 1) return { ventas, utilidad, trans, ticketProm, margen,
                            deltaVentas: null, deltaUtilidad: null, deltaTrans: null, deltaTicket: null };
    const primeraMitad = serie.slice(0, half);
    const segundaMitad = serie.slice(-half);
    const suma = (arr, k) => arr.reduce((s, d) => s + Number(d[k] || 0), 0);
    const pct = (nuevo, viejo) => {
      if (viejo === 0) return nuevo > 0 ? 100 : 0;
      return ((nuevo - viejo) / viejo) * 100;
    };
    const vAnt = suma(primeraMitad, 'ventas');
    const vNue = suma(segundaMitad, 'ventas');
    const uAnt = suma(primeraMitad, 'utilidad');
    const uNue = suma(segundaMitad, 'utilidad');
    const tAnt = suma(primeraMitad, 'transacciones');
    const tNue = suma(segundaMitad, 'transacciones');
    const tkAnt = tAnt > 0 ? vAnt / tAnt : 0;
    const tkNue = tNue > 0 ? vNue / tNue : 0;

    return {
      ventas, utilidad, trans, ticketProm, margen,
      deltaVentas: pct(vNue, vAnt),
      deltaUtilidad: pct(uNue, uAnt),
      deltaTrans: pct(tNue, tAnt),
      deltaTicket: pct(tkNue, tkAnt),
    };
  }, [serie]);

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
             alignItems={{ sm: 'center' }} gap={2} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Dashboard</Typography>
          <Typography color="text.secondary">
            Resumen de ventas y operación · últimos {periodo} días
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup value={periodo} exclusive
                             onChange={(_, v) => v && setPeriodo(v)}
                             size="small" color="primary">
            <ToggleButton value="7">7 días</ToggleButton>
            <ToggleButton value="15">15 días</ToggleButton>
            <ToggleButton value="30">30 días</ToggleButton>
            <ToggleButton value="90">90 días</ToggleButton>
          </ToggleButtonGroup>
          <IconButton onClick={() => setTick(t => t + 1)}><RefreshIcon /></IconButton>
        </Stack>
      </Stack>

      {cargando && <LinearProgress sx={{ mb: 2 }} />}

      {/* Snapshot del día */}
      {snapshot && (
        <Grid container spacing={1.5} mb={3}>
          <Grid item xs={6} sm={4} md={2.4}>
            <MiniCard icon={<PaidIcon />} color="primary.main"
                      label="Ventas hoy"
                      value={fmtCOP(snapshot.ventasHoy?.total)} />
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <MiniCard icon={<ReceiptIcon />} color="text.primary"
                      label="Transacciones hoy"
                      value={snapshot.ventasHoy?.cantidad ?? 0} />
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <MiniCard icon={<PeopleIcon />} color="info.main"
                      label="Clientes nuevos hoy"
                      value={snapshot.clientesNuevosHoy ?? 0} />
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <MiniCard icon={<PointOfSaleIcon />} color="success.main"
                      label="Cajas abiertas"
                      value={snapshot.cajasAbiertas ?? 0} />
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <MiniCard icon={<ReceiptLongIcon />} color="warning.main"
                      label="Facturas pendientes"
                      value={snapshot.facturasPendientes ?? 0} />
          </Grid>
        </Grid>
      )}

      {/* KPIs del período */}
      <Grid container spacing={2.5} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard icon={<PaidIcon />} label={`Ventas (${periodo}d)`}
                   value={fmtCOP(totales.ventas)}
                   delta={totales.deltaVentas} color="primary.main" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard icon={<TrendingUpIcon />} label="Utilidad bruta"
                   value={fmtCOP(totales.utilidad)}
                   delta={totales.deltaUtilidad} color="success.main" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard icon={<ReceiptIcon />} label="Transacciones"
                   value={totales.trans.toLocaleString('es-CO')}
                   delta={totales.deltaTrans} color="info.main" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard icon={<ShoppingCartIcon />} label="Ticket promedio"
                   value={fmtCOP(totales.ticketProm)}
                   delta={totales.deltaTicket} color="warning.main" />
        </Grid>
      </Grid>

      {/* Serie diaria + categorías */}
      <Grid container spacing={2.5} mb={3}>
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>Ventas vs Utilidad</Typography>
              {serie.length === 0 ? (
                <Alert severity="info">Sin ventas en el período.</Alert>
              ) : (
                <Box sx={{ width: '100%', height: 320 }}>
                  <ResponsiveContainer>
                    <LineChart data={serie}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="dia" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }}
                             tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(v) => fmtCOP(v)} />
                      <Legend />
                      <Line type="monotone" dataKey="ventas" stroke="#0F4C3A"
                            strokeWidth={2.5} name="Ventas" dot={false} />
                      <Line type="monotone" dataKey="utilidad" stroke="#2e7d32"
                            strokeWidth={2.5} name="Utilidad" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" mb={2}>Ventas por categoría</Typography>
              {categorias.length === 0 ? (
                <Alert severity="info">Sin ventas categorizadas.</Alert>
              ) : (
                <>
                  <Box sx={{ width: '100%', height: 260 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie data={categorias} dataKey="total" nameKey="nombre"
                             cx="50%" cy="50%" outerRadius={80} innerRadius={45}>
                          {categorias.map((c, i) => (
                            <Cell key={i} fill={c.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => fmtCOP(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                  <Stack spacing={0.75} sx={{ mt: 1, maxHeight: 140, overflow: 'auto' }}>
                    {categorias.slice(0, 6).map((c, i) => (
                      <Stack key={i} direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c.color }} />
                        <Typography variant="caption" sx={{ flexGrow: 1 }} noWrap>{c.nombre}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {fmtCOP(c.total)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top productos + stock crítico */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>Top productos vendidos</Typography>
              {topProductos.length === 0 ? (
                <Alert severity="info">Sin productos vendidos en el período.</Alert>
              ) : (
                <Box sx={{ width: '100%', height: Math.max(250, topProductos.length * 34) }}>
                  <ResponsiveContainer>
                    <BarChart data={topProductos} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis type="number"
                             tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                             tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="nombreProducto"
                             width={140} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => fmtCOP(v)} />
                      <Bar dataKey="totalVentas" fill="#0F4C3A" radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <WarningAmberIcon color="warning" />
                <Typography variant="h6">Stock crítico</Typography>
                <Chip label={stockCritico.length} color="warning" size="small" />
              </Stack>
              {stockCritico.length === 0 ? (
                <Alert severity="success">Todos los productos con stock suficiente.</Alert>
              ) : (
                <Box sx={{ maxHeight: 340, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Producto</TableCell>
                        <TableCell align="right">Stock</TableCell>
                        <TableCell align="right">Mínimo</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stockCritico.map(p => (
                        <TableRow key={p.productoId}>
                          <TableCell>
                            <Typography variant="body2">{p.nombre}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {p.codigo}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(100, p.stockMinimo > 0
                                ? (p.stockActual / p.stockMinimo) * 100 : 0)}
                              color={p.stockActual <= 0 ? 'error' : 'warning'}
                              sx={{ mt: 0.5, height: 4, borderRadius: 2 }} />
                          </TableCell>
                          <TableCell align="right">
                            <Chip label={p.stockActual} size="small"
                                  color={p.stockActual <= 0 ? 'error' : 'warning'} />
                          </TableCell>
                          <TableCell align="right">{p.stockMinimo}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Margen del período */}
      <Paper sx={{ mt: 3, p: 2.5, bgcolor: '#F5F7FA' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
               alignItems={{ sm: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="subtitle2">
              Margen bruto del período: <b>{totales.margen.toFixed(1)}%</b>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Diferencia entre precio de venta y costo, sin restar gastos operativos.
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
          <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
            <Typography variant="caption" color="text.secondary">Ticket promedio</Typography>
            <Typography variant="subtitle2" fontWeight={700}>
              {fmtCOP(totales.ticketProm)}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default Dashboard;
